import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ToggleTableBlock } from './ToggleTableBlock'
import { useTouchMove } from './useTouchMove'
import Checkbox from '../../checkbox/Checkbox'
import { useGetWidth } from 'utils/useGetWidth'
import { TSelector } from '../AppTable'

import { IHeaderData } from '../AppTableDndMobile'
import { IChooseItems } from 'core/api/dto/BaseDto'

interface ITableMobileRowProps<T, TKey extends keyof T> {
  item: T
  itemIndex: number
  idName?: string
  mobileMode: boolean
  currentCol: number
  setCurrentCol: React.Dispatch<React.SetStateAction<number>>
  tableDataSelectors: TSelector<T, TKey>[]
  subTableRepresent?: (
    item: T,
    currentCol: number,
    itemIndex?: number,
    onTouchStart?: (e: React.TouchEvent) => void,
    onTouchMove?: (e: React.TouchEvent) => void,
    width?: number,
    toggleAllState?: number[],
    headerData?: IHeaderData[],
  ) => React.ReactNode
  isDefaultOpen?: boolean
  rowClassName?: string
  tableCellClassName?: string
  headerData?: IHeaderData[]
  toggleAllState?: number[]
  setToggleAllState?: Dispatch<SetStateAction<number[]>>
  chooseMode?: boolean
  shouldRenderSubTable?: boolean | undefined
  chooseItems?: IChooseItems | null
  chooseItemsHandler: (value: boolean, id: number) => void
  rowRef?: React.Ref<HTMLTableRowElement>
}
interface IItemBase {
  id: number
  isArchived?: boolean
  isSynced?: boolean
}
// Универсальный компонент строки таблицы
const TableMobileRowInner = <T extends IItemBase, TKey extends keyof T>({
  item,
  mobileMode,
  currentCol,
  setCurrentCol,
  idName,
  itemIndex,
  subTableRepresent,
  tableDataSelectors,
  isDefaultOpen,
  rowClassName,
  tableCellClassName,
  toggleAllState,
  setToggleAllState,
  chooseItems,
  chooseItemsHandler,
  chooseMode,
  headerData,
  shouldRenderSubTable,
  rowRef,
}: ITableMobileRowProps<T, TKey>) => {
  // Флаг развёрнута ли подтаблица
  const [subTableActive, setSubTableActive] = useState(!!isDefaultOpen)
  // Управляет отложенной отрисовкой тяжёлых подтаблиц
  const [delayedSubRender, setDelayedSubRender] = useState(false)
  // Ширина экрана
  const { width } = useGetWidth()
  // Обработчики свайпа по ячейкам
  const { onTouchStart, onTouchMove } = useTouchMove({
    totalCount: tableDataSelectors.length,
    currentCol,
    setCurrentCol,
  })
  // Синхронизация внешнего раскрытия/сворачивания подтаблиц
  // useEffect(() => {
  //   if (!toggleAllState) return
  //   const shouldBeActive = toggleAllState.includes(itemIndex)
  //   setSubTableActive((prev) => {
  //     if (prev !== shouldBeActive) return shouldBeActive
  //     return prev
  //   })
  // }, [toggleAllState, itemIndex])

  const prevToggleStateRef = useRef<number[]>([])
  const manualToggleRef = useRef(false)
  useEffect(() => {
    if (!toggleAllState) return

    const prevToggleState = prevToggleStateRef.current
    const wasGloballyToggled =
      prevToggleState.length !== toggleAllState.length ||
      !prevToggleState.every((val, i) => val === toggleAllState[i])

    prevToggleStateRef.current = toggleAllState

    if (wasGloballyToggled) {
      const shouldBeActive = toggleAllState.includes(itemIndex)
      manualToggleRef.current = false // сбрасываем флаг ручного клика
      setSubTableActive(shouldBeActive)
    } else if (!manualToggleRef.current) {
      // если не было ручного клика — всё равно синхронизируем
      const shouldBeActive = toggleAllState.includes(itemIndex)
      setSubTableActive(shouldBeActive)
    }
  }, [toggleAllState, itemIndex])

  // Запуск отложенной отрисовки подтаблицы
  useEffect(() => {
    if (subTableActive) {
      const timeout = setTimeout(() => setDelayedSubRender(true), 0)
      return () => clearTimeout(timeout)
    } else {
      setDelayedSubRender(false)
    }
  }, [subTableActive])
  // Рендер по умолчанию, если не передан кастомный рендер
  const defaultRenderItem = (item: T, name: TKey) => (
    <div>{item[name] ? `${item[name]}` : null}</div>
  )
  // Мемоизированный список отображаемых колонок
  const visibleSelectors = useMemo(() => {
    if (!mobileMode) return tableDataSelectors
    const base = tableDataSelectors[0] // первая колонка всегда отображается
    const others = tableDataSelectors.slice(1)
    const visibleCount = width < 600 ? 1 : 3 // количество колонок по ширине
    const start = Math.max(0, currentCol - 1)
    const swipeColumns = others.slice(start, start + visibleCount)
    return [base, ...swipeColumns]
  }, [mobileMode, width, tableDataSelectors, currentCol])
  const setSubTableActiveWithManualFlag: Dispatch<SetStateAction<boolean>> = (
    value,
  ) => {
    manualToggleRef.current = true
    setSubTableActive(value)
  }
  return (
    <>
      <tr ref={rowRef} className={rowClassName}>
        {/* Колонка с чекбоксом выбора (если активен chooseMode) */}
        {chooseMode && (
          <td>
            {!!item.isArchived ||
            (item.isSynced &&
              process.env.REACT_APP_TYPE_SERVER !== 'MainServer') ? (
              <div className="delete-checkbox delete-item">
                <i className="an-ico an-ico-close" />
              </div>
            ) : (
              <Checkbox
                name={`${item.id}`}
                value={!!chooseItems?.[item.id]}
                onChange={(value) => chooseItemsHandler(value, item.id)}
              />
            )}
          </td>
        )}
        {/* Отображение колонок */}
        {visibleSelectors.map(
          (
            {
              propsNameToDisable,
              name,
              renderItem = (item) =>
                name ? defaultRenderItem(item, name!) : '?',
            },
            tdIndex,
          ) => (
            <td
              key={`tr-${itemIndex}-td-${tdIndex}`}
              id={`${
                (idName || '') + '-' || ''
              }row-${itemIndex}-col-${tdIndex}`}
              className={`${tableCellClassName || ''}${
                subTableActive ? ' active' : ''
              }${tdIndex === 0 ? ' col-sticky-name' : ''} ${
                tdIndex === 0 && itemIndex === 0 ? 'col-sticky-name-gray' : ''
              }  ${
                tdIndex === 0 && itemIndex === 0 ? 'col-sticky-name-violet' : ''
              }`}
              onTouchStart={!!tdIndex ? onTouchStart : undefined}
              onTouchMove={!!tdIndex ? onTouchMove : undefined}
            >
              {/* Первая ячейка может содержать кнопку разворота подтаблицы */}
              {subTableRepresent && !tdIndex ? (
                <ToggleTableBlock
                  subTableActive={subTableActive}
                  setSubTableActive={setSubTableActiveWithManualFlag}
                  itemIndex={itemIndex}
                  toggleAllState={toggleAllState}
                  setToggleAllState={setToggleAllState}
                >
                  {renderItem(item, itemIndex)}
                </ToggleTableBlock>
              ) : (
                renderItem(item, itemIndex)
              )}
            </td>
          ),
        )}
      </tr>
      {/* Подтаблица, если активна и разрешена отложенная отрисовка */}
      {shouldRenderSubTable &&
        subTableRepresent &&
        delayedSubRender &&
        subTableRepresent(
          item,
          mobileMode ? currentCol : 0,
          itemIndex,
          onTouchStart,
          onTouchMove,
          width,
          toggleAllState,
          headerData,
        )}
    </>
  )
}

// Мемоизация компонента для предотвращения лишних перерисовок
export const TableMobileRow = React.memo(
  TableMobileRowInner,
) as typeof TableMobileRowInner
